import type { TExample } from '@atlas/uicorn/preview/modules';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useSlate, Slate } from 'slate-react';
import { Editor } from 'slate';
import IconButtonsGroup, {
    TIconButtonsGroupItem,
    TIconButtonsGroupProps,
} from '@atlas/uicorn/lib/Control/IconButtonsGroup';
import type { ParagraphBlock, QuoteBlock } from '@atlas/renderer';
import isNotFalsy from '@atlas/tool/box/isNotFalsy';
import useRefCallback from 'use-ref-callback';
import createEditorInstance from '@atlas/tool/box/slate/createEditorInstance';
import editorFeatures from '@atlas/tool/box/slate/editorFeatures';
import translateRichInputFeatureToIconButtonGroupItem, {
    TRichInputFeatureItem,
    TTranslateRichInputFeatureToIconButtonGroupItemProps,
} from '@atlas/tool/box/format/translateRichInputFeatureToIconButtonGroupItem';

const RichInputControls = React.memo(function RichInputControls<
    TCustomProps extends Record<string, unknown> | undefined = undefined,
>(props: TRichInputControlsProps<TCustomProps>): React.ReactElement {
    const { items, disabled, customProps, onMouseDown, ...elementProps } = props;

    const editor = useSlate();

    const marks = Editor.marks(editor) ?? undefined;

    const nodeEntry =
        (editor.selection &&
            Editor.above(editor, { at: editor.selection, match: (n: any) => Editor.isBlock(editor, n) })) ||
        undefined;
    const node = nodeEntry?.[0];

    const buttonGroupItems = useMemo(() => {
        // TODO: Make use of useDeepMemo or other util to prevent rerenders when `marks` are not changed

        const itemProps = { editor, marks, disabled, node, ...customProps } as TRichInputControlsItemProps &
            TCustomProps;

        return items
            .map<TIconButtonsGroupItem | null>((item) =>
                translateRichInputFeatureToIconButtonGroupItem(itemProps, item),
            )
            .filter(isNotFalsy);
    }, [node, editor, marks, disabled, customProps, items]);

    const handleMouseDown = useRefCallback<React.MouseEventHandler<HTMLDivElement>>((event) => {
        const canBeFocused =
            event.target instanceof HTMLInputElement &&
            event.target.type !== 'button' &&
            event.target.type !== 'submit' &&
            event.target.type !== 'radio' &&
            event.target.type !== 'checkbox';

        if (!canBeFocused) event.preventDefault();

        onMouseDown?.(event);
    });

    return <SRichInputControls {...elementProps} onMouseDown={handleMouseDown} items={buttonGroupItems} />;
});

export default RichInputControls;

// #region Styles

export const SRichInputControls = styled(IconButtonsGroup)`
    color: ${(p) => p.theme.color.grey8};
`;

// #endregion

// #region Types

export type TRichInputControlsItemProps = TTranslateRichInputFeatureToIconButtonGroupItemProps;

export type TRichInputControlsItem<TProps extends TRichInputControlsItemProps = TRichInputControlsItemProps> =
    TRichInputFeatureItem<TProps>;

export type TRichInputControlsProps<TCustomProps extends Record<string, unknown> | undefined = undefined> = Omit<
    TIconButtonsGroupProps,
    'items'
> & {
    items: TRichInputControlsItem<TRichInputControlsItemProps & TCustomProps>[];
} & (TCustomProps extends undefined ? { customProps?: undefined } : { customProps?: TCustomProps });

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        const editor = useMemo(createEditorInstance, []);

        editor.children = useMemo(
            () => editor.children ?? [{ type: 'paragraph', children: [{ text: 'word' }] }],
            [editor.children],
        );
        editor.selection = { anchor: { offset: 0, path: [0, 0] }, focus: { offset: 4, path: [0, 0] } };

        const featureItems: TRichInputFeatureItem[] = [
            editorFeatures.bold,
            editorFeatures.italic,
            editorFeatures.blockquote,
            '|',
            () => ({ tooltip: 'Custom', icon: <i>🎉</i>, onClick: () => props.onParty('Party time!') }),
        ];

        return (
            <Slate
                editor={editor}
                initialValue={[{ type: 'paragraph', children: [{ text: 'word' }] }]}
                onChange={(children) => {
                    const [block] = children as [ParagraphBlock | QuoteBlock];
                    const [text] = block.children;
                    const marks = [text.bold && 'bold', text.italic && 'italic'].filter(Boolean);
                    props.onChange([block.type, marks.join(' and ')].filter(Boolean).join(' with '));
                }}
            >
                <RichInputControls items={featureItems} />
            </Slate>
        );
    },
    handlers: {
        onChange: 'console',
        onParty: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=0-1&mode=design&t=Hu2LDZwLvF5nHq01-0',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('RichInputControls', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
